import React, { useState, useEffect } from "react";
import "./pwa-install-prompt.scss";

const isStandaloneMode = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.includes("android-app://")
  );
};

const isMobileDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return false;
  const userAgent =
    navigator.userAgent || navigator.vendor || window.opera || "";
  const isMobileUA =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );
  const isIPadOS =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isMobileUA || isIPadOS;
};

const isIOSDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return false;
  return (
    ["iPad", "iPhone", "iPod"].includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(() =>
    typeof window !== "undefined" ? window.deferredInstallPrompt : null,
  );
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [showAndroidModal, setShowAndroidModal] = useState(false);

  useEffect(() => {
    // Strictly do NOT show on desktop or if already in standalone PWA mode
    if (!isMobileDevice() || isStandaloneMode()) {
      return;
    }

    // Check if dismissed in this session
    const dismissed = sessionStorage.getItem("pwa_install_dismissed");
    if (dismissed === "true") {
      return;
    }

    // Listener for when the user successfully installs the app
    const handleAppInstalled = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
      if (typeof window !== "undefined") {
        window.deferredInstallPrompt = null;
      }
      localStorage.setItem("pwa_installed", "true");
    };
    window.addEventListener("appinstalled", handleAppInstalled);

    // Check if deferredPrompt was already captured globally (e.g. from index.html)
    if (window.deferredInstallPrompt) {
      setDeferredPrompt(window.deferredInstallPrompt);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredInstallPrompt = e;
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    const handlePromptAvailable = () => {
      if (window.deferredInstallPrompt) {
        setDeferredPrompt(window.deferredInstallPrompt);
        setShowBanner(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("pwa-prompt-available", handlePromptAvailable);

    // Show the banner on all mobile devices.
    // If beforeinstallprompt is supported and fires, native 1-click install is used.
    // If not supported (e.g. Firefox, Samsung Internet) or not yet dispatched, manual install instructions are provided.
    setShowBanner(true);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("pwa-prompt-available", handlePromptAvailable);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOSDevice()) {
      setShowIOSModal(true);
      return;
    }

    const promptEvent = deferredPrompt || window.deferredInstallPrompt;
    if (promptEvent) {
      try {
        promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        if (choice && choice.outcome === "accepted") {
          setShowBanner(false);
          localStorage.setItem("pwa_installed", "true");
        }
        setDeferredPrompt(null);
        window.deferredInstallPrompt = null;
      } catch (err) {
        console.error("PWA install error:", err);
        setShowAndroidModal(true);
      }
    } else {
      // Fallback for browsers that do not support beforeinstallprompt (such as Firefox for Android)
      setShowAndroidModal(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem("pwa_install_dismissed", "true");
  };

  // Guard against desktop, standalone mode, or hidden banner
  if (!isMobileDevice() || isStandaloneMode() || !showBanner) {
    return null;
  }

  const canPrompt = Boolean(
    deferredPrompt ||
      (typeof window !== "undefined" && window.deferredInstallPrompt),
  );

  return (
    <>
      <div className="pwa-install-banner">
        <div className="pwa-install-banner__content">
          <i className="fa fa-mobile pwa-install-banner__icon" />
          <div className="pwa-install-banner__text">
            <strong>Install MiniBrew App</strong>
            <span>
              Add to home screen for lock-screen alerts & full-screen brewing
            </span>
          </div>
        </div>
        <div className="pwa-install-banner__actions">
          <button
            type="button"
            className="pwa-install-banner__btn"
            onClick={handleInstallClick}
          >
            {isIOSDevice() || !canPrompt ? "How to Install" : "Install"}
          </button>
          <button
            type="button"
            className="pwa-install-banner__close"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      </div>

      {showIOSModal && (
        <div
          className="pwa-ios-modal-overlay"
          onClick={() => setShowIOSModal(false)}
        >
          <div className="pwa-ios-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pwa-ios-modal__header">
              <h4>Install MiniBrew on iOS</h4>
              <button
                type="button"
                className="pwa-install-banner__close"
                onClick={() => setShowIOSModal(false)}
              >
                &times;
              </button>
            </div>
            <p
              style={{
                fontSize: "13px",
                color: "#9ca3af",
                marginBottom: "16px",
              }}
            >
              Install MiniBrew to receive step-by-step push alerts even when
              your phone is locked:
            </p>
            <ul className="pwa-ios-modal__steps">
              <li>
                <div className="step-number">1</div>
                <div className="step-text">
                  Tap the <strong>Share button</strong> (square with arrow up)
                  at the bottom of Safari.
                </div>
              </li>
              <li>
                <div className="step-number">2</div>
                <div className="step-text">
                  Scroll down and select <strong>Add to Home Screen</strong>.
                </div>
              </li>
              <li>
                <div className="step-number">3</div>
                <div className="step-text">
                  Open <strong>MiniBrew</strong> from your Home Screen to enable
                  brewing notifications!
                </div>
              </li>
            </ul>
            <div className="pwa-ios-modal__footer">
              <button type="button" onClick={() => setShowIOSModal(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {showAndroidModal && (
        <div
          className="pwa-ios-modal-overlay"
          onClick={() => setShowAndroidModal(false)}
        >
          <div className="pwa-ios-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pwa-ios-modal__header">
              <h4>Install MiniBrew on Android</h4>
              <button
                type="button"
                className="pwa-install-banner__close"
                onClick={() => setShowAndroidModal(false)}
              >
                &times;
              </button>
            </div>
            <p
              style={{
                fontSize: "13px",
                color: "#9ca3af",
                marginBottom: "16px",
              }}
            >
              Install MiniBrew for lock-screen alerts & full-screen brewing
              controls:
            </p>
            <ul className="pwa-ios-modal__steps">
              <li>
                <div className="step-number">1</div>
                <div className="step-text">
                  Tap the <strong>menu button</strong> (<strong>&#8942;</strong>{" "}
                  three dots) in your browser (top-right in Firefox/Chrome, or
                  bottom bar in Samsung Internet).
                </div>
              </li>
              <li>
                <div className="step-number">2</div>
                <div className="step-text">
                  Select <strong>Install</strong> or{" "}
                  <strong>Add to Home screen</strong>.
                </div>
              </li>
              <li>
                <div className="step-number">3</div>
                <div className="step-text">
                  Open <strong>MiniBrew</strong> from your Home Screen to enjoy
                  the full app experience!
                </div>
              </li>
            </ul>
            <div className="pwa-ios-modal__footer">
              <button type="button" onClick={() => setShowAndroidModal(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PwaInstallPrompt;
